import { createComponent } from "react-fela";
import { buttons, text } from "~/styleguide/links";
import colorData from "~/data/color.json";
const { colors } = colorData;
import get from "lodash.get";

import {
    getResourceSlugFromType,
    getResourceTypeFromPath
} from "~/modules/link-parser";
import Link from "next/link";

export default ({
    // Link URL
    url,

    // Link label (if any)
    label,

    // Slug for the resource
    slug,

    // Option to hide the label (if using cusotm content or a link wrapper)
    skipLabel = false,
    truncate = false,
    maxLines = 3,
    layout, // null, button, text
    size = "regular", // regular, large (for button layouts, only)
    children, // nested components
    extend,
    backgroundColor = colors.cobalt.primary,
    color = colors.white.primary,
    theme,
    cta_arrow = false,
    outline = false,
    className = null,
    ariaLabel = null,
    onClick = () => {}
}) => {
    if (!url) return null;

    let cleanURL = url.replace(process.env.SITE_URL, "");

    if (!cleanURL.includes("/wp-content/"))
        cleanURL = cleanURL.replace(process.env.WORDPRESS_URL, "");

    const isInternal = internalLinkCheck(cleanURL);

    // Striping relative double slashes from any internal links
    if (isInternal) {
        cleanURL = cleanURL.replace("//", "/");
    }

    const renderLabel = () => {
        if (!skipLabel) {
            if (!truncate) {
                return (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: label
                        }}
                        className={(cta_arrow ? "link-arrow" : "")}
                    ></span>
                );
            } else if (!skipLabel && truncate) {
                return (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: label
                        }}
                        className={(cta_arrow ? "link-arrow" : "")}
                        style={truncateStyles(maxLines)}
                    ></span>
                );
            }
        }
    };

    const renderLinkContents = () => {
        return (
            <InnerLink
                layout={layout}
                size={size}
                extend={extend}
                backgroundColor={backgroundColor}
                color={color}
                outline={outline}
                colorTheme={theme}
                as="span"
                onClick={onClick}
                className={className}
            >
                {renderLabel()}
                {children}
            </InnerLink>
        );
    };

    // Use route links if the resource is internal
    if (isInternal) {
        const details = makeInternalRoute(cleanURL, slug);

        if (process.env.NEXT_MODE === "spa")
            return (
                <Link
                    aria-label={ariaLabel}
                    className={className}
                    as={details.as ? details.as : null}
                    href={details.href ? details.href : null}
                >
                    {renderLinkContents()}
                </Link>
            );
        else
            return (
                <a
                    aria-label={ariaLabel}
                    href={details.as ? details.as : details.href}
                    className={className}
                >
                    {renderLinkContents()}
                </a>
            );
    }
    //Otherwise use a classic <a> tag for external links
    else {
        return (
            <a
                aria-label={ariaLabel}
                className={className}
                href={cleanURL}
                target="_blank"
                rel="noopener noreferrer"
            >
                {renderLinkContents()}
            </a>
        );
    }
};

const truncateStyles = maxLines => ({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: maxLines,
    overflow: "hidden"
});

/**
 * Determine if the link should be client-side routed
 * or link out to another site
 *
 * @param {String} url URL to test against
 */
const internalLinkCheck = url => {
    if (!url) return false;
    return (
        url[0] === "/" ||
        url[0] === "#" ||
        (url.startsWith(process.env.WORDPRESS_URL) &&
            !url.includes("/wp-content/"))
    );
};

const makeInternalRoute = (url, slug) => {
    const type = getResourceTypeFromPath(url);
    const base = getResourceSlugFromType(type);

    if (url && url.endsWith("/")) url = url.slice(0, -1);

    // AUTHORS
    if (url.startsWith("/blog/author")) {
        if (!slug) {
            slug = url.replace("/blog/author/", "");
        }
        return {
            as: `/blog/author/${slug}`,
            href: `/blog/author/[slug]`
        };
    }

    // PAGE
    if (url.startsWith("/page")) {
        if (!slug) {
            slug = url.replace("/page/", "");
        }
        return {
            as: `/${slug}`,
            href: `/[slug]`
        };
    }

    // CATEGORIES
    else if (url.startsWith("/blog/category")) {
        if (!slug) {
            slug = url.replace("/blog/category/", "");
        }
        return {
            as: `/blog/category/${slug}`,
            href: `/blog/category/[slug]`
        };
    }

    // TAGS
    else if (url.startsWith("/blog/tag")) {
        if (!slug) {
            slug = url.replace("/blog/tag/", "");
        }
        return {
            as: `/blog/tag/${slug}`,
            href: `/blog/tag/[slug]`
        };
    }

    switch (type) {
        // MAIN RESOURCES LISTING PAGE
        case "resources":
            return {
                as: `/${base}`,
                href: `/sales-strategy`
            };

        // RESOURCE TYPES
        case "ebooks":
        case "posts":
        case "webinars":
        case "whitepapers":
        case "infographics":
        case "glossary":
        case "library":
        case "landingpages":
        case "embeddedpage":
        case "thankyou":
        case "news":
        case "podcasts":
        case "tools":
        case "casestudies":
        case "videos":
        case "revenueexchange":
            // Detail page
            if (slug && slug !== base && url.substr(1) !== slug) {
                return {
                    as: `/${base}/${slug}`,
                    href: `/${base}/[slug]`
                };
            } else {
                return {
                    as: url,
                    href: `/resource`
                };
            }

        default:
            // If the path depth is more than one slug,
            // use a standard link value
            if (url.split("/").length > 2)
                return {
                    href: url
                };

            // /In all other cases, Page route is default
            return {
                as: url,
                href: url === "/" || url === "" ? "/" : `/[slug]`
            };
    }
};

export const getLinkFromRelationship = (type, content, url) => {
    const relationship = get(content, [0]);

    if (type === "content")
        return {
            slug: get(relationship, "slug"),
            url: get(relationship, "link")
        };
    else {
        return {
            slug: null,
            url: url
        };
    }
};

const InnerLink = createComponent(
    ({ layout, size, color, backgroundColor, colorTheme, outline }) => ({
        extend: [
            // TEXT TYPES
            text({ layout }),

            // BUTTON TYPES
            buttons({
                layout,
                size,
                color: outline ? null : color,
                backgroundColor,
                colorTheme,
                outline
            })
        ]
    }),
    "span",
    ["onClick"]
);
